/**
 * The select, read off the screens rather than off a spec.
 *
 * `design/DESIGN-SYSTEM.md` §2 specifies Inputs and never specifies a select,
 * so the values here come from the approved export, measured by property across
 * all 25 screens (the method DECISION-041 sanctions):
 *
 *   `padding: 10px 34px 10px 14px` · 40 uses · chevron at `right: 12px`
 *   `padding: 10px 38px 10px 14px` · 19 uses · chevron at `right: 14px`, 600
 *
 * Two variants, not one with a magic number: the row select sits inside a list
 * row and the `wide` one is a section switcher. `--mdx-icon-button` and
 * `--mdx-select-gutter` are the gutters, added to `design/tokens.css` by this
 * story.
 *
 * `appearance-none` plus a drawn chevron rather than the platform arrow, which
 * is what every screen does — a native arrow is a different shape and colour on
 * each OS, and this control appears eleven times on one page.
 *
 * Story 4.1.
 */
import { useId, type SelectHTMLAttributes } from 'react'

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  /** The accessible name. Every select on the members screen has one per row. */
  label: string
  /** Renders the label above the control, as `Field` does. Off by default. */
  showLabel?: boolean
  /** The wider section switcher — 600 weight and a deeper gutter. */
  wide?: boolean
}

/**
 * `className` lands on the WRAPPER, not the control.
 *
 * `Field` puts it on the input because a field owns its own box; a select is a
 * control plus an absolutely-positioned chevron, so the thing a caller needs to
 * size, grow or hide is the pair. Putting it on the `<select>` hid the control
 * and left the chevron floating — found by the 375px screenshot pass.
 */

export function Select({
  label,
  showLabel = false,
  wide = false,
  className = '',
  ...rest
}: SelectProps) {
  const id = useId()
  return (
    <div className={['flex min-w-0 flex-col gap-mdx-6', className].filter(Boolean).join(' ')}>
      {showLabel ? (
        <label htmlFor={id} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
          {label}
        </label>
      ) : null}
      <span className="relative flex min-w-0">
        <select
          id={id}
          aria-label={showLabel ? undefined : label}
          {...rest}
          className={[
            'w-full min-w-0 max-w-full appearance-none cursor-pointer truncate',
            'rounded-mdx-control border border-mdx-line bg-mdx-white',
            'py-mdx-10 pl-mdx-14 text-mdx-15 text-mdx-ink outline-none',
            wide ? 'pr-mdx-select-gutter font-mdx-semibold' : 'pr-mdx-icon-button font-mdx-regular',
            'transition-colors duration-mdx-swift ease-mdx-swift',
            'focus:border-mdx-brand focus:[box-shadow:var(--mdx-focus-ring)]',
            'disabled:bg-mdx-ground disabled:text-mdx-slate disabled:cursor-not-allowed',
          ].join(' ')}
        />
        <span
          aria-hidden="true"
          className={[
            'pointer-events-none absolute top-mdx-11 text-mdx-12 text-mdx-slate',
            wide ? 'right-mdx-14' : 'right-mdx-12',
          ].join(' ')}
        >
          ▾
        </span>
      </span>
    </div>
  )
}
